/**
 * One-tree LLM transcript render: `AtlasMessage[]` → `ModelMessage[]`.
 *
 * The whole thread renders as a single React tree through the
 * instructions reconciler. `<TranscriptProvider>` puts the live thread
 * (messages, revision projection, tool registry) into context;
 * `<Transcript>` maps records to `<transcript-message role>` boundary
 * elements that `renderTranscript` splits into role-tagged model
 * messages.
 *
 * Every tool record renders as one machine-readable envelope on a
 * `user` turn — the environment-feedback turn the model acts *after*;
 * never `system` (re-applied as a standing instruction) or `assistant`
 * (imitated as prose-written tool calls). A run of consecutive records
 * shares one `user` turn, wrapped in an annotated `<tool-call-log>`
 * when it holds two or more:
 *
 *     <tool-call-log count="2">
 *       <tool-call tool-name="…" rev-id="…" pending repeated="3">
 *         …call slot…
 *         <tool-call-result kind="error" compact>…result slot…</tool-call-result>
 *       </tool-call>
 *       …
 *     </tool-call-log>
 *
 * Everything the model needs as *data* is an attribute (`pending` for
 * in-flight, `repeated` for coalesced identical failures, `rev-id` /
 * `superseded-by` for revision lineage, `kind` for non-success
 * outcomes); slot content comes from the tool's `message` formatters
 * with component-level defaults. Dialogue-shaped tools declare a
 * `message.transcript` override and emit their own turns instead of the
 * envelope. Every visibility decision — full vs compact, failure hints
 * — is made by the component that owns the output, reading thread state
 * through hooks (`useCompact`), never from precomputed flags.
 *
 * Tool call/result pairs are read off `ToolUIPart`/`DynamicToolUIPart`
 * directly — the SDK already pairs them keyed by `toolCallId`, so we
 * never round-trip through `convertToModelMessages`.
 *
 * Every message is rendered — there is no recency cap. Trimming the
 * transcript would evict the active plan and earlier completed
 * tool-call records, which makes the model re-issue calls it has
 * already made.
 */

import { getToolName, isTextUIPart, isToolUIPart } from 'ai';
import React, { type ReactNode, use } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { AnyToolUiPart, AtlasMessage } from './types.ts';
import type { TranscriptRole } from '#plugin-chat/lib/instructions/types.ts';
import { IDENTIFIER_RESOLUTION_ERROR_TYPES } from '#plugin-chat/lib/tool/errors/index.ts';
import { DefaultToolCall, DefaultToolOutcome } from '#plugin-chat/lib/tool/message-components.tsx';
import type { ToolSidecarEntry } from '#plugin-chat/lib/tool/message-formatters.ts';
import type { ToolOutputError } from '#plugin-chat/lib/tool/output-types.ts';
import { buildRevisionIndex, supersededBy } from '#plugin-chat/lib/tool/revisions.ts';
import { isTerminalToolPart, type SettledOutcome, settledOutcome } from '#plugin-chat/lib/tool/settled-outcome.ts';
import {
  ToolRecordContext,
  TranscriptContext,
  type TranscriptThread,
  useCompact,
} from '#plugin-chat/lib/tool/TranscriptContext.tsx';
import { toolCallIdentity } from '#plugin-chat/lib/tool/tool-identity.ts';

/**
 * Flat record model the thread projects to before rendering:
 *
 * - `prose` — role-tagged passthrough content (text parts).
 * - `tool` — one tool record. `identity` is the structural call
 *   signature (tool name + args, formatting-insensitive); a run of
 *   consecutive records with the same identity that all `failed`
 *   coalesces into one record with a `repeated` attribute, so an
 *   N-attempt flail is one annotated record, not N near-identical ones.
 */
type ProseRecord = { kind: 'prose'; role: TranscriptRole; node: ReactNode };
type ToolRecord = { kind: 'tool'; part: AnyToolUiPart; identity: string; failed: boolean };
type TranscriptRecord = ProseRecord | ToolRecord;

type RenderItem = { kind: 'prose'; record: ProseRecord } | { kind: 'tool'; record: ToolRecord; repeated: number };

/**
 * The parsed args off a tool part. The bridge only asserts the record
 * shape the formatter contract wants — per-tool schema validation
 * already ran at the execute boundary.
 */
const partArgs = (part: AnyToolUiPart): Record<string, unknown> =>
  typeof part.input === 'object' && part.input !== null ? (part.input as Record<string, unknown>) : {};

const recordsForPart = (part: AtlasMessage['parts'][number], role: AtlasMessage['role']): TranscriptRecord[] => {
  if (isTextUIPart(part)) {
    // Whitespace-only text would render to a blank message the
    // transcript splitter drops anyway; skip the reconciler work.
    return part.text.trim() ? [{ kind: 'prose', role, node: part.text }] : [];
  }
  if (isToolUIPart(part)) {
    // Only a pending call (`input-available`) or a settled one has a
    // record to emit; streaming and approval states have nothing yet.
    if (!isTerminalToolPart(part) && part.state !== 'input-available') {
      return [];
    }
    return [
      {
        kind: 'tool',
        part,
        identity: toolCallIdentity(getToolName(part), partArgs(part)),
        failed: settledOutcome(part)?.kind === 'error',
      },
    ];
  }
  // Reasoning and step-start parts carry nothing the model should re-read.
  return [];
};

/**
 * Coalesce consecutive records of the *same failing call* into one item
 * with a repeat count. Only failures fold — the model still sees every
 * distinct thing it tried, just not the same dead end stamped N times.
 * The folded item keeps the **last** attempt's part, so the freshest
 * error envelope is the one rendered. Cosmetic: the authoritative "stop
 * repeating" signal is the execute-boundary guard
 * (`flagRepeatedFailure`).
 */
const coalesceRecords = (records: TranscriptRecord[]): RenderItem[] =>
  records.reduce<RenderItem[]>((items, record) => {
    const prev = items.at(-1);
    if (
      record.kind === 'tool' &&
      record.failed &&
      prev?.kind === 'tool' &&
      prev.record.failed &&
      prev.record.identity === record.identity
    ) {
      return [...items.slice(0, -1), { kind: 'tool', record, repeated: prev.repeated + 1 }];
    }
    if (record.kind === 'prose') {
      return [...items, { kind: 'prose', record }];
    }
    return [...items, { kind: 'tool', record, repeated: 1 }];
  }, []);

const RENDER_FAILURE_FALLBACK =
  '[A tool result could not be rendered — the tool completed but its output could not be formatted.]';

/**
 * Content that throws during render must NOT vanish from the
 * transcript: degrade to a visible fallback so the agent still learns
 * the tool resolved instead of silently never seeing it. One shape for
 * every degradation site — record bodies, formatter slots, transcript
 * overrides — varying only the fallback and the log context.
 */
function DegradeBoundary({
  fallback,
  context,
  children,
}: {
  fallback: ReactNode;
  context: string;
  children: ReactNode;
}) {
  return (
    <ErrorBoundary
      fallbackRender={() => fallback}
      onError={(e) => console.error(`[transcript] ${context} — degrading to fallback`, e)}
    >
      {children}
    </ErrorBoundary>
  );
}

/** A `<transcript-message>` turn with the render-failure boundary inside the turn host. */
function TranscriptMessage({
  children,
  ...props
}: React.PropsWithChildren<React.JSX.IntrinsicElements['transcript-message']>) {
  return (
    <transcript-message {...props}>
      <DegradeBoundary context="record failed to render" fallback={<p>{RENDER_FAILURE_FALLBACK}</p>}>
        {children}
      </DegradeBoundary>
    </transcript-message>
  );
}

/**
 * The scope facts every tool-record component reads: the thread, the
 * part being rendered, and the tool's registry entry. Throws outside a
 * record — these components only exist inside `<ToolPartRecord>`.
 */
function useToolRecord() {
  const thread = use(TranscriptContext);
  // The context is typed as the lib/tool structural slice; the
  // transcript provider only ever mounts full UI parts, so the widening
  // is a refinement, not a guess.
  const part = use(ToolRecordContext) as AnyToolUiPart | null;
  if (thread === null || part === null) {
    throw new Error('tool record components must render inside TranscriptProvider and ToolRecordContext');
  }
  const toolName = getToolName(part);
  return { thread, part, toolName, args: partArgs(part), entry: thread.toolRegistry[toolName] };
}

/** The chronologically last successful result of a revisable tool — the one record that carries revision coaching. */
const latestRevisableResultCallId = (
  messages: TranscriptThread['messages'],
  toolRegistry: TranscriptThread['toolRegistry'],
): string | undefined =>
  messages
    .flatMap((m) => m.parts ?? [])
    .findLast(
      (p) =>
        p.toolCallId !== undefined &&
        isTerminalToolPart(p) &&
        settledOutcome(p)?.kind === 'result' &&
        toolRegistry[getToolName(p as AnyToolUiPart)]?.revisable === true,
    )?.toolCallId;

/**
 * The tool's `call` formatter, or the default args rendering. Content of
 * the `<tool-call>` envelope. The formatter is invoked in a child
 * component ({@link ToolCallBody}) so a throw lands inside the boundary
 * — a boundary cannot catch its own parent's render.
 */
function ToolCallSlot() {
  const { args, toolName } = useToolRecord();
  return (
    <DegradeBoundary context={`call formatter threw for "${toolName}"`} fallback={<DefaultToolCall args={args} />}>
      <ToolCallBody />
    </DegradeBoundary>
  );
}

function ToolCallBody() {
  const { args, toolName, entry } = useToolRecord();
  const call = entry?.message?.call;
  return call ? call({ args, toolName }) : <DefaultToolCall args={args} />;
}

/**
 * Concise failure line for a compacted failed call, replacing the full
 * error section so the suggestions / recovery prose is dropped while
 * the error itself stays. The call body is deliberately NOT dropped:
 * the model must still see the exact expression it ran to avoid
 * re-issuing it.
 */
function CompactToolFailureLine({ toolName, envelope }: { toolName: string; envelope: ToolOutputError }) {
  // Identifier-resolution failures carry the load-bearing recovery hint (the
  // "available columns / views" list) as their first suggestion. Keep that one
  // line even when compacting, so a retry loop still sees the fix in the
  // persistent transcript — not only on the turn the failure was the live
  // result. Everything else stays a single error line.
  const hint = IDENTIFIER_RESOLUTION_ERROR_TYPES.has(envelope.errorType) ? envelope.suggestions?.[0] : undefined;
  return (
    <>
      <p>
        <code>{toolName}</code> → failed ({envelope.errorType}): {envelope.error}
      </p>
      {hint != null && <p>{hint}</p>}
    </>
  );
}

/**
 * Result-slot content: the compact policy first (a compacted record
 * never invokes the tool's formatter — a compact success is an empty
 * result tag, a compact failure the one-line error), then the tool's
 * formatter for the outcome's kind, then the canonical default.
 */
function ToolResultBody({ outcome }: { outcome: SettledOutcome }) {
  const { args, toolName, entry } = useToolRecord();
  const compact = useCompact();
  const message = entry?.message;
  if (compact) {
    return outcome.kind === 'error' ? <CompactToolFailureLine envelope={outcome.envelope} toolName={toolName} /> : null;
  }
  if (outcome.kind === 'denied' && message?.denied) {
    return message.denied({ args, reason: outcome.reason, toolName });
  }
  if (outcome.kind === 'error' && message?.error) {
    return message.error({ args, envelope: outcome.envelope, toolName });
  }
  if (outcome.kind === 'result' && message?.result) {
    return message.result({ args, output: outcome.output });
  }
  return <DefaultToolOutcome outcome={outcome} toolName={toolName} />;
}

/**
 * The `<tool-call-result>` section of a record envelope. Self-guarding:
 * renders nothing while the call is in flight. Successful results are
 * the unmarked default — `kind` is stamped only on `error` / `denied`;
 * `compact` marks a collapsed body.
 */
function ToolResult() {
  const { part, toolName } = useToolRecord();
  const compact = useCompact();
  const outcome = settledOutcome(part);
  if (outcome === null) {
    return null;
  }
  return (
    <tag-tool-call-result compact={compact} kind={outcome.kind !== 'result' && outcome.kind}>
      <DegradeBoundary
        context={`${outcome.kind} formatter threw for "${toolName}"`}
        fallback={<DefaultToolOutcome outcome={outcome} toolName={toolName} />}
      >
        <ToolResultBody outcome={outcome} />
      </DegradeBoundary>
    </tag-tool-call-result>
  );
}

/**
 * Revision coaching for the chronologically last successful result of a
 * revisable tool. Self-guarding — every other revisable record carries
 * only its citable `rev-id` attribute; repeating the coaching paragraph
 * on each one drowned the transcript in identical instructions.
 */
function RevisionNote() {
  const { thread, part, toolName, entry } = useToolRecord();
  const revision = thread.revisions.byCallId.get(part.toolCallId);
  if (entry?.revisable !== true || revision === undefined || settledOutcome(part)?.kind !== 'result') {
    return null;
  }
  if (thread.latestRevisableCallId !== part.toolCallId) {
    return null;
  }
  return (
    <p>
      This result's revision id is {revision.revisionId}. If a later {toolName} call updates, corrects, or re-renders
      this same result, set its replaces parameter to "{revision.revisionId}" so the old version collapses into the new
      one as revision history. Note: Omit <code>replaces</code> unless the replacement call applies a specific minor
      correction or modification to the replaced tool call, based on specific user instructions.
    </p>
  );
}

/**
 * A dialogue tool's `message.transcript` output. Invoked in its own
 * component so a throwing formatter lands inside the enclosing
 * {@link DegradeBoundary} — a boundary cannot catch its own parent's
 * render.
 */
function TranscriptOverride() {
  const { part, args, toolName, entry } = useToolRecord();
  return entry?.message?.transcript?.({ args, toolName, outcome: settledOutcome(part) }) ?? null;
}
/**
 * Body of one tool record.
 *
 * - A **superseded** call (a later revision's successful `replaces`
 *   points at it, possibly transitively) collapses to a one-line stub —
 *   the model keeps the revision chain without the stale call/result
 *   prose; only terminal parts can be superseded, so this never
 *   swallows an in-flight call.
 * - A tool with a **`message.transcript` override** renders its own
 *   top-level turns instead of the envelope.
 * - Everything else renders the `<tool-call>` envelope: data as
 *   attributes, slot content from the tool's formatters. The enclosing
 *   `user` turn is owned by {@link ToolRunLog}, so consecutive records
 *   share one turn.
 */
function ToolPartBody({ repeated }: { repeated: number }) {
  const { thread, part, toolName, args, entry } = useToolRecord();
  const outcome = settledOutcome(part);
  // Every tool call gets a revision index entry, but only revisable
  // tools surface the id — a `rev-id` the model can never cite in
  // `replaces` is noise.
  const revision = entry?.revisable === true ? thread.revisions.byCallId.get(part.toolCallId) : undefined;

  const superseding = isTerminalToolPart(part) ? supersededBy(thread.revisions, part.toolCallId) : undefined;
  if (superseding) {
    return (
      <tag-tool-call rev-id={revision?.revisionId} superseded-by={superseding.revisionId} tool-name={toolName}>
        Earlier you ran {toolName}
        {revision === undefined ? '' : ` (revision ${revision.revisionId})`}; it has been superseded by revision{' '}
        {superseding.revisionId}, so its output is omitted. Build on the current revision — cite{' '}
        {superseding.revisionId} in <code>replaces</code> if you revise it again.
      </tag-tool-call>
    );
  }

  if (entry?.message?.transcript) {
    return (
      <DegradeBoundary
        context={`transcript formatter threw for "${toolName}"`}
        fallback={
          <transcript-message role="user">
            <p>{RENDER_FAILURE_FALLBACK}</p>
          </transcript-message>
        }
      >
        <TranscriptOverride />
      </DegradeBoundary>
    );
  }

  return (
    <tag-tool-call
      pending={outcome === null}
      repeated={repeated > 1 && repeated}
      rev-id={revision?.revisionId}
      tool-name={toolName}
    >
      <ToolCallSlot />
      <ToolResult />
      <RevisionNote />
    </tag-tool-call>
  );
}

/** One tool record, scoped so every descendant hook knows which part it renders. */
function ToolPartRecord({ part, repeated }: { part: AnyToolUiPart; repeated: number }) {
  return (
    <ToolRecordContext value={part}>
      <ToolPartBody repeated={repeated} />
    </ToolRecordContext>
  );
}

type ToolItem = Extract<RenderItem, { kind: 'tool' }>;
type TranscriptGroup =
  | { kind: 'prose'; record: ProseRecord }
  | { kind: 'dialogue'; item: ToolItem }
  | { kind: 'run'; items: ToolItem[] };

/**
 * Group render items into maximal runs of consecutive tool records: a
 * run renders as ONE `user` turn (wrapped in `<tool-call-log>` when it
 * holds ≥2 records). Prose breaks a run — wherever the model narrates,
 * alternation is preserved. Dialogue-shaped tools (`message.transcript`)
 * emit their own top-level turns, so they both break a run and stand
 * alone — nesting their turns inside a log turn would be structurally
 * invalid.
 */
const groupItems = (items: RenderItem[], toolRegistry: TranscriptThread['toolRegistry']): TranscriptGroup[] =>
  items.reduce<TranscriptGroup[]>((groups, item) => {
    if (item.kind === 'prose') {
      groups.push({ kind: 'prose', record: item.record });
      return groups;
    }
    if (toolRegistry[getToolName(item.record.part)]?.message?.transcript !== undefined) {
      groups.push({ kind: 'dialogue', item });
      return groups;
    }
    const last = groups.at(-1);
    if (last?.kind === 'run') {
      last.items.push(item);
    } else {
      groups.push({ kind: 'run', items: [item] });
    }
    return groups;
  }, []);

/** One `user` turn for a run of tool records; ≥2 records wrap in an annotated `<tool-call-log>`. */
function ToolRunLog({ items }: { items: ToolItem[] }) {
  const records = items.map((item) => (
    <ToolPartRecord key={item.record.part.toolCallId} part={item.record.part} repeated={item.repeated} />
  ));
  if (items.length === 1) {
    return <TranscriptMessage role="user">{records}</TranscriptMessage>;
  }
  return (
    <TranscriptMessage role="user">
      <tag-tool-call-log count={items.length}>
        <p>You made the following {items.length} tool calls, in order; each record includes its result.</p>
        {records}
      </tag-tool-call-log>
    </TranscriptMessage>
  );
}

/** The thread as `<transcript-message>` turns: prose passthrough plus one `user` turn per tool run. */
export function Transcript({ messages }: { messages: readonly AtlasMessage[] }) {
  const thread = use(TranscriptContext);
  if (thread === null) {
    throw new Error('Transcript must render inside TranscriptProvider');
  }
  const records = messages.flatMap((msg) => msg.parts.flatMap((part) => recordsForPart(part, msg.role)));
  return groupItems(coalesceRecords(records), thread.toolRegistry).map((group, i) => {
    if (group.kind === 'prose') {
      return (
        // biome-ignore lint/suspicious/noArrayIndexKey: groups render once in fixed chronological order; the index is their stable identity
        <TranscriptMessage key={i} role={group.record.role}>
          {group.record.node}
        </TranscriptMessage>
      );
    }
    if (group.kind === 'dialogue') {
      return (
        // biome-ignore lint/suspicious/noArrayIndexKey: groups render once in fixed chronological order; the index is their stable identity
        <ToolPartRecord key={i} part={group.item.record.part} repeated={group.item.repeated} />
      );
    }
    // biome-ignore lint/suspicious/noArrayIndexKey: groups render once in fixed chronological order; the index is their stable identity
    return <ToolRunLog items={group.items} key={i} />;
  });
}

/**
 * Mounts the thread-state contexts around a transcript render. The
 * context value IS the messages being rendered — one source of truth;
 * the revision projection is derived from them here, once per render,
 * like every other transcript shape.
 */
export function TranscriptProvider({
  messages,
  toolRegistry,
  children,
}: {
  messages: readonly AtlasMessage[];
  toolRegistry: Record<string, ToolSidecarEntry>;
  children: ReactNode;
}) {
  const revisions = buildRevisionIndex(messages);
  const latestRevisableCallId = latestRevisableResultCallId(messages, toolRegistry);
  return (
    <TranscriptContext value={{ messages, revisions, toolRegistry, latestRevisableCallId }}>
      {children}
    </TranscriptContext>
  );
}
